import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import { toast } from 'react-hot-toast';
import axios from '../utils/axios';

const OrganizationSettings = () => {
  const [organization, setOrganization] = useState({
    name: '',
    website: '',
    api_key: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await axios.get('/organization');
      setOrganization(response.data.organization);
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      toast.error('Failed to load organization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put('/organization', organization);
      setOrganization(response.data.organization);
      toast.success('Organization settings updated successfully');
    } catch (error) {
      console.error('Failed to update organization:', error);
      toast.error('Failed to update organization settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrganization(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const regenerateApiKey = async () => {
    try {
      const response = await axios.post('/organization/regenerate-key');
      setOrganization(prev => ({
        ...prev,
        api_key: response.data.api_key
      }));
      toast.success('API key regenerated successfully');
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
      toast.error('Failed to regenerate API key');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Organization Settings</h1>
            <p className="text-gray-600">Manage your organization's details and API access</p>
          </div>

          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    Organization Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={organization.name}
                    onChange={handleChange}
                    className="w-full"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="website" className="text-sm font-medium">
                    Website
                  </label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={organization.website}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="api_key" className="text-sm font-medium">
                    API Key
                  </label>
                  <div className="flex gap-2">
                    <Input
                      id="api_key"
                      name="api_key"
                      type="text"
                      value={organization.api_key}
                      readOnly
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={regenerateApiKey}
                      variant="outline"
                    >
                      Regenerate
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500">
                    Use this key to authenticate API requests. Keep it secret!
                  </p>
                </div>

                <Button 
                  type="submit"
                  className="w-full"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default OrganizationSettings;
